<template>
  <div class="search-page__result">
    <h1 class="hidden">Search Result Page</h1>
    <div class="error" v-if="error">
      {{ error }}
    </div>
    <div class="not-found" v-else-if="notFound">
      There are no products that match your search
    </div>
    <section v-else>
      <div
        class="search-page__result-and-filter mt-sm"
        :class="{ 'flex-column': newFilterVersion }"
      >
        <client-only>
          <p class="m-0">
            Showing <b>{{ totalProducts }}</b> results
            <span v-if="searchQuery && !collectionHandle"
              >for "{{ searchQuery }}"</span
            >
            <span v-if="collectionHandle && collectionTitle"
              >for "{{ collectionTitle }}"</span
            >
          </p>
        </client-only>

        <CollectionSortAndFilter
          :class="{ 'mt-sm mb-sm': newFilterVersion }"
          :filterSections="filterSections"
          :pageType="'search'"
          :collectionHandle="collectionHandle"
        ></CollectionSortAndFilter>
      </div>

      <SelectedFilters :horizontal-scroll="newFilterVersion" />

      <ProductGrid
        :lazyloadStartIndex="10"
        :products="
          loading || !isCollectionLoaded ? placeholderProducts : products
        "
        @item-click="onClickSearchResult(PRODUCT_TYPE, $event.handle)"
        :isShowProductListOldVersion="isDigitalProductCollection"
        :isImagesSlider="isImagesSlider"
        showWishlistIcon
      />
      <sib-pagination
        class="mt-md"
        :currentPage="currentPage"
        :numberOfPage="totalPage"
        @input="onPageChange"
      ></sib-pagination>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { useFetchData } from '@/composables/fetchData'
import useSearchStore from '@/store/search'
import {
  FilterSection,
  Image,
  NullOrType,
  Product,
  SearchProductParams,
  SearchResults,
} from '@/types'
import {
  onMounted,
  onServerPrefetch,
  onUnmounted,
  watch,
  useSSRContext,
} from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import ProductGrid from '@/components/product/ProductGrid.vue'
import { computed } from '@vue/reactivity'
import {
  PAGE_QUERY_STRING,
  SEARCH_QUERY_STRING,
  SEARCH_SHORT_QUERY_STRING,
  SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX,
  SEARCH_RESULT_PAGE_TITLE,
  SORT_BY_QUERY_STRING,
  SORT_ORDER_QUERY_STRING,
  QUERY_STRING_PRODUCT_IDS,
  QUERY_STRING_PRODUCT_HANDLE,
  EVENT_TRACKING_PRODUCTS_SEARCHED,
  EVENT_TRACKING_PAGE_SEARCH_VIEWED,
  EVENT_TRACKING_SEARCH_RESULT_CLICKED,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
} from '@/utils'
import useUpdateRouter from '@/composables/router'
import { usePage } from '@/composables/page'
import { useMetaTag } from '@/composables/metaTag'
import { isOnServer } from '@/utils/ssr'
import { appendToHead } from '@/composables/appendToHead'
import {
  PRODUCT_TAG_GOOGLE_DUP_KW,
  SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS,
  SSR_CONTEXT_APPEND_TO_HEAD_IMAGE_META_TAGS,
  SSR_CONTEXT_APPEND_TO_HEAD_JSON_LD,
  CURRENCY,
  GOOGLE_BUSINESS_VERTICAL,
  EVENT_TRACKING_PAGE_COLLECTION_VIEWED,
  RESTRIC_VN_LOCATION_PRODUCT_TAGS,
  RESTRIC_TMN1_SEGMENT_PRODUCT_TAGS,
  TMN1_SEGMENT,
  SEARCHANISE,
  SIB_SEARCH,
  VN_COUNTRY_CODE,
  PRODUCT_ID_TRACKING_BASE_ONE_PRODUCT_VARIANT_SKU,
} from '@/utils/constants'
import { isProductHasTags } from '@/utils/product'
import useCollectionStore from '@/store/collection'
import SibPagination from '@/ui-kits/Pagination.vue'
import Analytics from '@/services/analytics'
import useSettingStore from '@/store/setting'
import useRequestLocationStore from '@/store/requestLocation'
import CollectionSortAndFilter from '@/components/collection/CollectionSortAndFilter.vue'
import SelectedFilters from '@/components/collection/SelectedFilters.vue'
import useUserStore from '@/store/user'

interface SearchResultProp {
  isImagesSlider?: boolean
  collectionTitle?: string
}

withDefaults(defineProps<SearchResultProp>(), {
  isImagesSlider: false,
  collectionTitle: '',
})

const userStore = useUserStore()
const settingStore = useSettingStore()
const searchStore = useSearchStore()
const collectionStore = useCollectionStore()
const { renderImageMetaTags } = useMetaTag()
const route = useRoute()
const itemsPerPage = 20
const placeholderProducts = Array(itemsPerPage).fill(undefined)
const { setPageTitle } = usePage()
const { loading, fetchData, resetVariables, error, notFound } = useFetchData({
  fetchDataFunction: search,
  withTimeStamp: true,
  checkNotFoundFunction: checkNotFound,
})
const { updateQueryString } = useUpdateRouter()
const requestLocationStore = useRequestLocationStore()

const PAGE_COMPONENT = 'page'
const PRODUCT_TYPE = 'product'

const newFilterVersion = computed(
  () => settingStore.pages?.general?.filterVersion?.toUpperCase() == 'V2'
)

const isSibSearch = computed(
  () => import.meta.env.VITE_SEARCH_TYPE == SIB_SEARCH
)

const isSearchanise = computed(
  () => import.meta.env.VITE_SEARCH_TYPE == SEARCHANISE
)

const searchQuery = computed(() => route.query.query || route.query.q)
const collectionHandle = computed(
  () => route.params['collectionHandle'] as string
)

const totalProducts = computed(() => {
  const MAX_NUMBER_RESULTS = 10000
  if (!searchStore.searchResults?.totalProducts) return 0
  if (searchStore.searchResults.totalProducts > MAX_NUMBER_RESULTS)
    return `${MAX_NUMBER_RESULTS}+`
  return searchStore.searchResults.totalProducts
})

const filterSections = computed<FilterSection[]>(
  () => searchStore.searchResults?.facets || []
)

const collectionIdSortManual = computed(() => {
  const collectionSortManualItem = settingStore.shop?.collectionHandleSortManual
    ?.split(',')
    .find((item) => item.includes(route.params['collectionHandle'] as string))

  if (collectionSortManualItem) {
    return collectionSortManualItem.split('_')[1]
  } else {
    return ''
  }
})
const currentPage = computed(() => {
  if (!route.query[PAGE_QUERY_STRING]) return 1
  let result = +route.query[PAGE_QUERY_STRING]
  if (result < 0) result = 1

  // totalPage always equal 1 because there is no data in SRR
  // so result variable should only be caculate in CSR
  if (result > totalPage.value && !isOnServer) result = totalPage.value
  return result
})
const searchParams = computed(() => {
  const SORT_BY_QUERY_DEFAULT = isSibSearch.value
    ? ''
    : collectionIdSortManual.value
    ? `collection_${collectionIdSortManual.value}_position`
    : 'sales_amount'
  const SORT_ORDER_QUERY_DEFAULT = collectionIdSortManual.value ? 'asc' : 'desc'
  const params: SearchProductParams = {
    query:
      (route.query[SEARCH_QUERY_STRING] as string) ||
      (route.query[SEARCH_SHORT_QUERY_STRING] as string),
    sortBy:
      (route.query[SORT_BY_QUERY_STRING] as string) || SORT_BY_QUERY_DEFAULT,
    sortOrder:
      (route.query[SORT_ORDER_QUERY_STRING] as string) ||
      SORT_ORDER_QUERY_DEFAULT,
    itemsPerPage,
    startIndex: itemsPerPage * (currentPage.value - 1),
    page: currentPage.value,
  }
  if (route.params['collectionHandle']) {
    params.collection = route.params['collectionHandle'] as string
  }
  if (
    route.query['tmr_search'] == 'true' &&
    !route.params['collectionHandle']
  ) {
    params.store =
      import.meta.env.VITE_SEARCH_STORE_TMR || import.meta.env.VITE_SEARCH_STORE
  }
  for (const query in route.query) {
    if (!query.startsWith(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX)) continue
    if (Object.prototype.hasOwnProperty.call(route.query, query)) {
      const attribute = query
        .split(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX)
        .pop()
      params[`${SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX}${attribute}`] = route
        .query[query] as string
    }
  }

  return params
})

// only show products when the collection is loaded
// because the collection metafields will decide which template to load
const isCollectionLoaded = computed(() => {
  if (!route.params['collectionHandle']) return true
  return (
    collectionStore.collection &&
    collectionStore.collection.handle == route.params['collectionHandle']
  )
})

const totalPage = computed(() => {
  return (
    Math.ceil((searchStore.searchResults?.totalProducts || 0) / itemsPerPage) ||
    1
  )
})

const products = computed(() => {
  const searchResultProduct = searchStore.searchResults?.products ?? []
  const productsWithOutUtmProducts = searchResultProduct.filter((product) =>
    collectionStore.utmProducts.every(
      (utmProduct) => product.id != utmProduct.id.split('/').pop()
    )
  )
  return [...collectionStore.utmProducts, ...productsWithOutUtmProducts]
})

const isDigitalProductCollection = computed(
  () => route.params['collectionHandle'] == 'digital-products'
)

const ssrContext = isOnServer && useSSRContext()

const isRequestVNLocation = computed(
  () => requestLocationStore.countryCode.toUpperCase() == VN_COUNTRY_CODE
)

const isTrackingProductIdBaseOnVariantSku = computed(() => {
  return (
    settingStore.shop?.productTrackingProductIdBaseOn ==
    PRODUCT_ID_TRACKING_BASE_ONE_PRODUCT_VARIANT_SKU
  )
})

onServerPrefetch(async () => {
  searchStore.searchResults = await fetchData()
  // reset variables to prevent hydration mis-match
  resetVariables()
  if (!route.params['collectionHandle']) {
    setPageTitle(SEARCH_RESULT_PAGE_TITLE)
  }
  injectMetaTagsIntoHeadTag()
  injectItemListJsonLdIntoHeadTag()
})

onMounted(async () => {
  const unWatch = watch(
    searchParams,
    async (newValue, oldValue) => {
      if (!newValue) return
      if (oldValue && JSON.stringify(oldValue) == JSON.stringify(newValue))
        return
      const data = await fetchData()
      searchStore.searchResults = data
      collectionStore.setUtmProductsOnCollection([])
      trackingSearchResult()
    },
    { deep: true }
  )

  onBeforeRouteLeave(() => {
    unWatch()
  })

  if (!searchStore.searchResults) {
    searchStore.searchResults = await fetchData()
  }

  if (route.params['collectionHandle']) {
    watch(
      () => route.params['collectionHandle'],
      () => {
        searchStore.searchResults = null
      }
    )
  }
  trackingSearchResult()
})

onUnmounted(() => {
  searchStore.searchResults = null
})

function checkNotFound(response: NullOrType<SearchResults>) {
  return !response || !response.products?.length
}

async function search(): Promise<SearchResults> {
  const searchParamsClone = JSON.parse(JSON.stringify(searchParams.value))
  let params = {}

  let rbTags = searchParamsClone.rb_tags || []

  // hide product has tag clo1 or CLO1 for IP VN
  if (isRequestVNLocation.value) {
    rbTags = [
      ...rbTags,
      ...RESTRIC_VN_LOCATION_PRODUCT_TAGS.split(',').map((tag) => `!${tag}`),
    ]
  }

  if (
    isSearchanise.value &&
    !userStore.segments?.find((segment) => segment.key == TMN1_SEGMENT) &&
    !settingStore.shop?.searchProductTM
  ) {
    const restrictTMN1Segment = RESTRIC_TMN1_SEGMENT_PRODUCT_TAGS.split(
      ','
    ).map((tag) => `!${tag}`)

    rbTags = [...rbTags, ...restrictTMN1Segment]
  }

  if (rbTags?.length) {
    params = {
      ...searchParamsClone,
      rb_tags: rbTags,
    }
  } else {
    params = searchParamsClone
  }

  const response = route.params['collectionHandle']
    ? await searchStore.searchProductByCollection(params)
    : await searchStore.searchProductFull(params)

  return response
}

function onClickSearchResult(type: string, handle: string) {
  if (route.params['collectionHandle']) return
  // Analytics.track(
  //   EVENT_TRACKING_SEARCH_RESULT_CLICKED,
  //   {
  //     query:
  //       (route.query[SEARCH_QUERY_STRING] as string) ||
  //       (route.query[SEARCH_SHORT_QUERY_STRING] as string),
  //     component: PAGE_COMPONENT,
  //     type: type,
  //     target: window.location.origin + handle,
  //   },
  //   destinationIntegrations([MIXPANEL_DESTINATION])
  // )
}

function onPageChange(page: number) {
  updateQueryString({
    [PAGE_QUERY_STRING]: page,
    [QUERY_STRING_PRODUCT_IDS]: undefined,
    [QUERY_STRING_PRODUCT_HANDLE]: undefined,
  })
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function injectMetaTagsIntoHeadTag() {
  // inject meta tags into head tag
  if (!ssrContext) return

  const searchResultsMetaTags = `<meta property="og:type" content="product.group" />`
  appendToHead(
    searchResultsMetaTags,
    SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS,
    ssrContext
  )

  const firstCollection =
    searchStore.searchResults?.collections &&
    searchStore.searchResults.collections[0]

  let image = undefined

  if (firstCollection?.image) {
    image = firstCollection.image
  } else {
    const firstProduct = searchStore.searchResults?.products[0]
    const fisrtProductImage = firstProduct?.images && firstProduct.images[0]

    image = fisrtProductImage
  }

  const searchResultsImageMetaTags = renderImageMetaTags(image)
  appendToHead(
    searchResultsImageMetaTags,
    SSR_CONTEXT_APPEND_TO_HEAD_IMAGE_META_TAGS,
    ssrContext
  )
}

function injectItemListJsonLdIntoHeadTag() {
  if (!ssrContext || !searchStore.searchResults?.products?.length) return

  const itemListJsonLdScript = renderItemListJsonLd(
    searchStore.searchResults?.products
  )

  appendToHead(
    itemListJsonLdScript,
    SSR_CONTEXT_APPEND_TO_HEAD_JSON_LD,
    ssrContext
  )
}

function renderItemListJsonLd(products: Product[]) {
  const itemListJsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    numberOfItems: products.length + '',
    itemListElement: products.map((product: Product) => ({
      '@type': 'Product',
      name: product.title,
      image: product.images?.map((image: Image) => image.src),
      offers: {
        '@type': 'Offer',
        priceCurrency: CURRENCY,
        price: product.price + '',
      },
    })),
  })

  const itemListJsonLdScript = `<script type="application/ld+json">${itemListJsonLd}<\/script>`

  return itemListJsonLdScript
}

function trackingSearchResult() {
  if (!searchStore.searchResults) return
  if (!route.params['collectionHandle']) {
    Analytics.track(EVENT_TRACKING_PRODUCTS_SEARCHED, {
      query:
        (route.query[SEARCH_QUERY_STRING] as string) ||
        (route.query[SEARCH_SHORT_QUERY_STRING] as string),
      number_of_result: searchStore.searchResults.totalProducts,
      component: PAGE_COMPONENT,
    })
  }
  trackingPageViewed(
    searchStore.searchResults.products,
    searchStore.searchResults.totalProducts
  )
}

function trackingPageViewed(products: Product[], totalProducts: number) {
  const items = products?.length
    ? products?.map((item) => ({
        id: isTrackingProductIdBaseOnVariantSku.value
          ? item?.variants?.length
            ? isProductHasTags(item, [PRODUCT_TAG_GOOGLE_DUP_KW])
              ? +item?.id + '-' + item.variants[0].sku
              : item.variants[0].sku
            : ''
          : +item?.id,
        name: item.title,
        price: item.variants?.length ? +item.variants[0].price! : 0,
        google_business_vertical:
          settingStore.shop?.googleBusinessVertical || GOOGLE_BUSINESS_VERTICAL,
      }))
    : []
  if (!route.params['collectionHandle']) {
    Analytics.track(EVENT_TRACKING_PAGE_SEARCH_VIEWED, {
      query:
        (route.query[SEARCH_QUERY_STRING] as string) ||
        (route.query[SEARCH_SHORT_QUERY_STRING] as string),
      number_of_result: totalProducts,
      items,
    })
  } else {
    Analytics.track(EVENT_TRACKING_PAGE_COLLECTION_VIEWED, {
      name: collectionStore.collection?.title,
      handle: collectionStore.collection?.handle,
      param: route.fullPath.split('?')[1] || '',
      content_type: 'product_group',
      items,
    })
  }
}
</script>

<style lang="scss">
.search-page__result {
  .pagination {
    font-size: 1.25rem;
  }

  &-and-filter {
    @include media-md-up {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }
    p {
      text-align: center;
    }
  }
  .flex-column {
    flex-direction: column;
  }
}
</style>
